import * as format from './format';
import datetime from './datetime';
import { MONTHS } from './constants';
import { INTERVALS } from '../../ui/constants/axis-intervals';
import moment from 'moment';
import { checkArticle } from '../inner/axes-common';

/**
 * Processes and optimizes the chart data before building categories and series.
 */
export default function processData(options) {
  if (options.categoriesAreDates) {
    options.dates = datetime.parse(options.categories, options);
    if ((!options.quarterlyResults || (options.quarterlyResults && options.series[0].data.length > 44)) && options.type !== 'bar') {
      const { fiscalYears } = options;
      const sortedDates = [...options.dates].sort((a, b) => a.value - b.value);
      options.xAxisTicks = {};
      const isArticle = checkArticle(options.size, options.product);
      const majorValue = isArticle ? options.majorTicksArticle : options.majorTicksPromo;
      const minorValue = isArticle ? options.minorTicksArticle : options.minorTicksPromo;
      const isDecade = majorValue === 'decade';
      const isHalfDecade = majorValue === 'halfDecade';
      const isCentury = majorValue === 'century';
      const longerThanYear = isDecade || isHalfDecade || isCentury;
      const major = longerThanYear ? 'year' : majorValue;
      const minor = minorValue === 'decade' ? 'year' : minorValue;
      const firstDate = sortedDates[0];
      const lastDate = sortedDates[sortedDates.length - 1];
      let majorTick = firstDate[major];
      let minorTick = firstDate[minor];
      let isFirstLabel = true;
      const interval = INTERVALS.find(({ name }) => name === majorValue).time;
      const tickPerColumn = options.dateSpan / interval >= options.series[0].data.length - 2;
      if (options.type === 'column' && tickPerColumn) {
        sortedDates.forEach(date => {
          const label = createLabel(date, majorValue, isFirstLabel, fiscalYears);
          options.xAxisTicks[date.value] = { label, type: 'major' };
          isFirstLabel = false;
        })
      } else {
        if (options.liveMarketData) {
          sortedDates.forEach((date, i) => {
            const needsMajor = date[major] !== majorTick &&
              (!longerThanYear ||
              isDecade && date.year % 10 === 0 ||
              isHalfDecade && date.year % 5 === 0 ||
              isCentury && date.year % 100 === 0);
            const needsMinor = date[minor] !== minorTick &&
              (options.type !== 'column' || isFirstLabel) &&
              (minorValue !== 'decade' ||
              minorValue === 'decade' && date.year % 10 === 0);
            if (needsMajor) {
              majorTick = date[major];
              minorTick = date[minor];
              const label = createLabel(date, majorValue, isFirstLabel, fiscalYears);
              options.xAxisTicks[date.value] = { label, type: 'major' };
              isFirstLabel = false;
            } else if (needsMinor) {   
              minorTick = date[minor];
              const label = isFirstLabel ?
                createLabel(date, minorValue, isFirstLabel, fiscalYears)
                :
                '';
              options.xAxisTicks[date.value] = { type: 'minor', label }
              isFirstLabel = false;        
            }
          });
        } else {
          let firstTick;
          let needsMajor; 
          for (let i = 1; i < sortedDates.length; i++) {
            const date = sortedDates[i];
            needsMajor = date[major] !== majorTick &&
              (!longerThanYear ||
              isDecade && date.year % 10 === 0 ||
              isHalfDecade && date.year % 5 === 0 ||
              isCentury && date.year % 100 === 0);
            const needsMinor = date[minor] !== minorTick &&
              (options.type !== 'column' || isFirstLabel) &&
              (minorValue !== 'decade' ||
              minorValue === 'decade' && date.year % 10 === 0);
            if (needsMajor || needsMinor) {
              firstTick = date.value;
              break;
            }
          }
          const tick = moment.utc(firstTick);
          const lastMoment = moment.utc(lastDate.value);
          while (tick.isBefore(lastMoment) || tick.isSame(lastMoment)) {
            needsMajor = tick[major]() !== majorTick &&
              (!longerThanYear ||
              isDecade && tick.year() % 10 === 0 ||
              isHalfDecade && tick.year() % 5 === 0 ||
              isCentury && tick.year() % 100 === 0);
            if (needsMajor) {
              const label = createLabel(tick.toObject(), majorValue, isFirstLabel, fiscalYears);
              options.xAxisTicks[tick.valueOf()] = { label, type: 'major' };
              majorTick = tick[major]();
              isFirstLabel = false;
            } else if (options.type !== 'column' || isFirstLabel) {
              const label = isFirstLabel ?
                createLabel(tick.toObject(), minorValue, isFirstLabel, fiscalYears)
                :
                '';
              options.xAxisTicks[tick.valueOf()] = { label, type: 'minor' };
              isFirstLabel = false;
            }
            const [n, timeType] = getTimeType(minorValue);
            tick.add(n, timeType);
            isFirstLabel = false;
          }
        }
        const tickValues = Object.keys(options.xAxisTicks);
        const labeledTicks = tickValues.filter(key => options.xAxisTicks[key].label);
        if (labeledTicks.length === 1) {
          const lastTickValue = Math.max(...tickValues);
          const firstTickValue = Math.min(...tickValues);
          const lastTickDate = moment.utc(lastTickValue).toObject();
          const firstTickDate = moment.utc(firstTickValue).toObject();
          options.xAxisTicks[lastTickValue].label = createLabel(lastTickDate, minorValue, false);
          options.xAxisTicks[firstTickValue].label = createLabel(firstTickDate, minorValue, true, fiscalYears);
        } else if (!labeledTicks.length) {
          const label = createLabel(firstDate, minorValue, true, fiscalYears);
          options.xAxisTicks[firstDate.value] = { label, type: 'minor' };
        }
      }
    }

    options.datesAll = datetime.all(options.dates);
    if ((!options.quarterlyResults || (options.quarterlyResults && options.series[0].data.length > 44)) && options.type !== 'bar') {
      options.series = insertDates(options.series, options);
    }
  }
  if (options.type === 'pie' && options.optimizeDataOrder) {
    options = optimizePieData(options);
  } else if (options.categoriesAreDates && ((!options.quarterlyResults || (options.quarterlyResults && options.series[0].data.length > 44)))) {
    options = sortDataByDate(options);
  }
  if (options.axes && options.axes.units && options.axes.units.divisor !== "none") {
    options = divideYAxisValues(options);
  }
  return options;
}

function getTimeType(minorValue) {
  let timeType = minorValue;
  if (minorValue === 'date') timeType = 'day';
  if (minorValue === 'decade') timeType = 'year';
  const n = minorValue === 'decade' ?
    10 : 1;
  return [n, timeType];
}

function createLabel(dateObj, type, isFirst, fiscalYears) {
  const year = dateObj.year || dateObj.years;
  const month = dateObj.month === undefined ? dateObj.months : dateObj.month;
  const date = dateObj.date;
  const hour = dateObj.hour || dateObj.hours;
  const minute = dateObj.minute || dateObj.minutes;
  const shortYear = year.toString().slice(2);
  let yearText = isFirst || shortYear === '00' ?
    year : `'${shortYear}`;
  if (isFirst && fiscalYears) yearText = 'FY' + yearText;
  const monthText = MONTHS[month];
  const quarterText = `${(month - (date === 1 ? 0 : 2)) / 3 + 1}Q`;
  switch (type) {
    case 'year':
    case 'decade':
    case 'halfDecade':
    case 'century':
      return yearText;
    case 'quarter':
      return month === 0 || month === 2 ?
        `${quarterText} ${yearText}`
        :
        quarterText
    case 'month':
      return month === 0 || isFirst ?
        `${monthText} ${yearText}`
        :
        monthText;
    case 'date':
      return `${monthText} ${date}`;
    case 'hour':
      const ampm = hour >= 12 ? 'p.m.' : 'a.m.';
      const formattedHour = hour === 12 ?
        'noon' : `${hour % 12} ${ampm}`;
      return isFirst ? `${monthText} ${date}` : formattedHour;
    default:
      return '';
  }
}

// Insert date values into all data.
function insertDates(data, options) {
  const proxyYears = getProxyYears(options);
  data.forEach(series => {
    series.data.forEach((item, index) => {
      let dateObj = options.dates[index];
      let date = new Date(dateObj.value);
      if (dateObj.year === undefined && proxyYears[index] !== undefined) {
        dateObj.yearIsProxy = true;
        date.setUTCFullYear(proxyYears[index]);
      }
      item.x = date.valueOf();
    });
  });
  return data;
}


// For data with dates with months but not years, create proxy years so the
// dates can be sorted correctly.
function getProxyYears(options) {
  let proxyYears = [];
  if (options.datesAll.month && !options.datesAll.year) {
    let year = (new Date()).getUTCFullYear();
    let month = undefined;
    for (let i = options.dates.length - 1; i >= 0; i--) {
      if (month !== undefined && month <= options.dates[i].month) {
        year -= 1;
      }
      proxyYears[i] = year;
      month = options.dates[i].month;
    }
  }
  return proxyYears;
}

function sortDataByDate(options) {
  options.series.forEach(item => {
    item.data.forEach((pt, index) => {
      pt.index = index;
    });
    if (item.data.every(point => point.x)) {
      item.data.sort((a, b) => {
        // if (a.x === undefined || b.x === undefined) {
        //   return 0;
        // }
        return a.x - b.x;
      });
    }
    // item.data.forEach((pt, index) => {
      // pt.index = index;
    // });
  });
  return options;
}

// Data should be ordered like so:
//      2 1
//    3     10
//  4         9
//    5     8
//      6 7
// Also, category names get attached to points.
function optimizePieData(options) {
  if (Array.isArray(options.categories)) {
    // Link each point to a category.
    options.series[0].data.forEach((point, index) => {
      point.name = options.categories[index];
      point.originalIndex = index;
    });
  }
  // Sort the data smallest to largest.
  options.series[0].data.sort(function (a, b) {
    if (a.y > b.y) {
      return 1;
    } else if (a.y < b.y) {
      return -1;
    }
    return 0;
  });
  // Take the largest data at the end and make it first.
  options.series[0].data.unshift(options.series[0].data.pop());
  return options;
}

function divideYAxisValues(options) {
  const divisor = format.getDivisor(options.axes.units.divisor);
  for (let i = 0; i < options.series.length; i++) {
    const yAxisValue = options.series[i].data;
    yAxisValue.forEach(value => {
      if (value.y !== null) {
        value.y = value.y / divisor;
      }
    });
  }
  return options;
}



// WEBPACK FOOTER //
// ./src/chart/data/processData.js